import { UserContextProject, selectUserProject } from 'core/userContext';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BADGE_VARIANTS, Badge } from 'ui/badge';
import { Popover, PopoverContent, PopoverPlacements, PopoverTrigger } from 'ui/popover';
import { spacing } from 'ui/theme';
import { PAGE_TITLE } from '../constants';
import { useBillingQueryContext } from '../contexts/BillingQueryContext';
import { CurrentProjectStatus } from '../types';
import { getCurrentProjectStatus, getRemainingSyncs } from '../utils';
import { StyledPageHeader } from './DataWarehousePage.styled';
import { PageActions } from './PageActions';

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.base};
`;

export const PAGE_HEADER_TEXT = {
  productionTitle: 'syncs remaining',
  productionContent: 'Your plan includes {{5}} syncs across all projects in your org.',
  testLabel: 'Test project',
  testTitle: 'This is a test project',
  testContent:
    'Syncs in this project do not count towards the {{5}}-sync limit for your organization.',
};

const { productionTitle, productionContent, testLabel, testTitle, testContent } = PAGE_HEADER_TEXT;
const { TEST, PRODUCTION } = CurrentProjectStatus;

export const createText = (
  status: CurrentProjectStatus,
  allotment: number = 0,
  productionSyncsCount: number = 0,
) => {
  const remainingSyncs = getRemainingSyncs({ allotment, productionSyncsCount });
  const isTest = status === TEST;
  return {
    content: isTest
      ? testContent.replace('{{5}}', `${allotment}`)
      : productionContent.replace('{{5}}', `${allotment}`),
    label: isTest ? testLabel : `${productionSyncsCount} of ${allotment} syncs used`,
    title: isTest ? testTitle : `${remainingSyncs} ${productionTitle}`,
    variant:
      !isTest && productionSyncsCount > allotment ? BADGE_VARIANTS.WARNING : BADGE_VARIANTS.INFO,
  };
};

export const PageHeader = () => {
  /** Org */
  const { hightouchOrg } = useBillingQueryContext();
  const { allotment = 5, productionSyncsCount = 0 } = hightouchOrg?.organization || {};
  const projects = hightouchOrg?.projects ?? [];

  /** Project */
  const currentProject = useSelector(selectUserProject) as UserContextProject;
  const status = getCurrentProjectStatus(currentProject?.id, projects);

  /** Show Popover if no error state or similar */
  const shouldShowPopover = [PRODUCTION, TEST].includes(status);

  /** Strings */
  const { content, label, title, variant } = createText(status, allotment, productionSyncsCount);

  return (
    <StyledPageHeader pageActions={<PageActions />}>
      <StyledTitle>
        <span>{PAGE_TITLE}</span>
        {shouldShowPopover && (
          <Popover>
            <PopoverContent side={PopoverPlacements.Right} TitleContent={title}>
              {content}
            </PopoverContent>
            <PopoverTrigger>
              <Badge variant={variant}>{label}</Badge>
            </PopoverTrigger>
          </Popover>
        )}
      </StyledTitle>
    </StyledPageHeader>
  );
};
